/**
 * The Growth page's feed, straight from pv-auth:
 *   GET /provider/admin/growth/cohorts?by=campaign|promoter|door|channel&from=&to=
 * Shapes mirror src/services/growth/ledger.math.ts in pv-auth-service. Every
 * null is "not measured" and renders as the words, never as 0.
 */

export type CohortBy = 'campaign' | 'promoter' | 'door' | 'channel';
export const COHORT_BYS: { key: CohortBy; label: string }[] = [
  { key: 'channel', label: 'Channel' },
  { key: 'campaign', label: 'Campaign' },
  { key: 'promoter', label: 'Promoter' },
  { key: 'door', label: 'Door' },
];

export interface SourceTotal {
  by: CohortBy;
  sourceKey: string;
  weeks: number;
  signups: number;
  verified: number;
  firstCheck: number;
  day3: number;
  habitGate: number;
  paid: number;
  revenueCents: number | null;
  revenueCurrency: string | null;
  ltvPerSignupCents: number | null;
  paidRate: number | null;
  firstCheckRate: number | null;
  eligibleD7: number;
  retentionD7: number | null;
  eligibleD30: number;
  retentionD30: number | null;
  spendCents: number | null;
  spendCurrency: string | null;
  costPerSignupCents: number | null;
  valuePerSpendDollar: number | null;
}

export interface CohortWeek {
  by: CohortBy;
  sourceKey: string;
  weekStart: string;
  signups: number;
  verified: number;
  firstCheck: number;
  day3: number;
  habitGate: number;
  paid: number;
  revenueCents: number | null;
  revenueCurrency: string | null;
  ltvPerSignupCents: number | null;
  eligibleD7: number;
  retainedD7: number | null;
  retentionD7: number | null;
  eligibleD30: number;
  retainedD30: number | null;
  retentionD30: number | null;
}

export interface SpendWeek { by: CohortBy; sourceKey: string; weekStart: string; spendCents: number; currency: string }

export interface CohortsPayload {
  by: CohortBy;
  window: { from: string | null; to: string | null };
  computedAt: string | null;
  ageDays: number | null;
  note: string | null;
  sources: SourceTotal[];
  weeks: CohortWeek[];
  spend: SpendWeek[];
}

export async function fetchCohorts(by: CohortBy, from?: string, to?: string): Promise<CohortsPayload> {
  const { apiGet } = await import('@/services/base');
  const q = new URLSearchParams({ by });
  if (from) q.set('from', from);
  if (to) q.set('to', to);
  return (await apiGet(`/provider/admin/growth/cohorts?${q.toString()}`, true)) as CohortsPayload;
}

export interface UserLtv {
  userId: string;
  value: Record<string, unknown> | null;
  attribution: Record<string, unknown> | null;
}

export async function fetchUserLtv(userId: string): Promise<UserLtv> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/growth/ltv?userId=${encodeURIComponent(userId)}`, true)) as UserLtv;
}

export async function runLedgerNow(): Promise<unknown> {
  const { apiPost } = await import('@/services/base');
  return apiPost('/provider/admin/growth/ledger/run', {}, true);
}

/* ── Pure helpers ───────────────────────────────────────────────────────── */

export const NOT_MEASURED = 'not measured';

/** Minor units to "$12.34"; the words when unmeasured; the currency when not USD. */
export const cents = (v: number | null | undefined, currency: string | null = 'usd'): string => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return NOT_MEASURED;
  if (currency === 'mixed') return 'mixed currencies';
  const amount = (v / 100).toFixed(2);
  return !currency || currency === 'usd' ? `$${amount}` : `${amount} ${currency.toUpperCase()}`;
};

/** A 0..1 rate as "33%" with its denominator, or the words when unmeasured. */
export const pct = (rate: number | null | undefined, n?: number): string => {
  if (typeof rate !== 'number' || !Number.isFinite(rate)) return NOT_MEASURED;
  const p = `${Math.round(rate * 100)}%`;
  return typeof n === 'number' ? `${p} of ${n}` : p;
};

export type SortKey = 'ltvPerSignupCents' | 'retentionD30' | 'paidRate' | 'signups' | 'retentionD7' | 'costPerSignupCents' | 'valuePerSpendDollar';

export const SORTS: { key: SortKey; label: string }[] = [
  { key: 'ltvPerSignupCents', label: 'LTV per signup' },
  { key: 'retentionD30', label: 'Day 30 retention' },
  { key: 'paidRate', label: 'Paid rate' },
  { key: 'retentionD7', label: 'Day 7 retention' },
  { key: 'signups', label: 'Signups' },
  { key: 'valuePerSpendDollar', label: 'Value per spend dollar' },
  { key: 'costPerSignupCents', label: 'Cost per signup' },
];

/**
 * Sort sources by a column, descending, with unmeasured rows LAST whatever
 * the direction: a null must never sort as the smallest number. Ties fall
 * back to signups so small-n rows do not float above real ones.
 */
export const sortSources = (rows: SourceTotal[], key: SortKey): SourceTotal[] =>
  [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    const an = typeof av === 'number';
    const bn = typeof bv === 'number';
    if (an && !bn) return -1;
    if (!an && bn) return 1;
    if (an && bn && av !== bv) return key === 'costPerSignupCents' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    return b.signups - a.signups || a.sourceKey.localeCompare(b.sourceKey);
  });

/** The steps of one source's funnel, each with its count and its share of signups. */
export const funnelFor = (t: SourceTotal): { step: string; count: number; rate: number | null }[] => {
  const r = (n: number) => (t.signups > 0 ? n / t.signups : null);
  return [
    { step: 'signups', count: t.signups, rate: t.signups > 0 ? 1 : null },
    { step: 'verified', count: t.verified, rate: r(t.verified) },
    { step: 'first check', count: t.firstCheck, rate: r(t.firstCheck) },
    { step: 'third check day', count: t.day3, rate: r(t.day3) },
    { step: 'habit gate (6 scan days)', count: t.habitGate, rate: r(t.habitGate) },
    { step: 'paid', count: t.paid, rate: r(t.paid) },
  ];
};

/** "n=3" badges: anything under this many signups is flagged as too small to read a rate from. */
export const SMALL_N = 10;
export const isSmall = (t: { signups: number }): boolean => t.signups < SMALL_N;

/** The label for the bucket of people with no attribution row. */
export const sourceLabel = (key: string): string => (key === '(none)' ? 'no attribution recorded' : key);

/** Weeks for one source, newest first. */
export const weeksFor = (weeks: CohortWeek[], sourceKey: string): CohortWeek[] =>
  weeks.filter((w) => w.sourceKey === sourceKey).sort((a, b) => b.weekStart.localeCompare(a.weekStart));

/** ISO date (YYYY-MM-DD) n days ago in UTC. */
export const isoDaysAgo = (days: number, now: number = Date.now()): string => new Date(now - days * 86_400_000).toISOString().slice(0, 10);

/* ── The owner's brief ──────────────────────────────────────────────────── */

export interface BriefSuggestion {
  ruleId: string;
  suggestionKey: string;
  action: string;
  why: string[];
  expectedEffect: string;
  downside: string;
  measure: string;
  cited: { table: string; key: string; values: Record<string, number | string | null> }[];
  cohortRef: { by: CohortBy; sourceKey: string } | null;
}

export interface OwnerBrief {
  id: string;
  weekStart: string;
  generatedAt: string;
  trigger: string;
  slackStatus: string | null;
  text: string;
  brief: {
    changed: { label: string; thisWeek: number | null; priorWeek: number | null; basis: string }[];
    stops: { from: string; to: string; before: number; after: number; lost: number; lastEvent: { name: string; count: number } | null }[];
    working: { by: CohortBy; sourceKey: string; signups: number; paid: number; ltvPerSignupCents: number | null; revenueCurrency: string | null; retentionD30: number | null; eligibleD30: number }[];
    suggestions: BriefSuggestion[];
    outcomes: { suggestionKey: string; status: string; line: string }[];
    sources: { posthog: string; ledger: string; founding: string };
  };
  outcomes: { suggestionKey: string; status: string; decidedAt: string; note: string | null }[];
}

export async function fetchOwnerBrief(): Promise<OwnerBrief | null> {
  const { apiGet } = await import('@/services/base');
  try {
    return (await apiGet('/provider/admin/growth/brief', true)) as OwnerBrief;
  } catch (e: unknown) {
    if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
    throw e;
  }
}

export async function runOwnerBriefNow(post: boolean): Promise<unknown> {
  const { apiPost } = await import('@/services/base');
  return apiPost('/provider/admin/growth/brief/run', { post }, true);
}

export async function markBriefOutcome(briefId: string, suggestionKey: string, status: 'done' | 'skipped', note?: string): Promise<unknown> {
  const { apiPost } = await import('@/services/base');
  return apiPost('/provider/admin/growth/brief/outcome', { briefId, suggestionKey, status, note }, true);
}

/** The decision already recorded for a suggestion in this brief, if any. */
export const outcomeFor = (b: OwnerBrief, suggestionKey: string) => b.outcomes.find((o) => o.suggestionKey === suggestionKey) ?? null;
